import { z } from 'zod';
import { reportCubeCatalog } from './catalog.js';
const cubeOrderDirectionSchema = z.enum(['asc', 'desc']);
const asNonEmptyTuple = (values) => values;
const { cubes, tenant } = reportCubeCatalog;
const filterOperatorEnum = z.enum(asNonEmptyTuple(reportCubeCatalog.filterOperators));
const cubeTimeDimensionSchema = z.object({
    dateRange: z.union([z.string(), z.tuple([z.string(), z.string()])]).optional(),
    dimension: z.string(),
    granularity: z.enum(reportCubeCatalog.timeGranularities).optional(),
});
const cubeFilterSchema = z.object({
    isRuntimeParameter: z.boolean().optional(),
    member: z.string(),
    operator: filterOperatorEnum,
    values: z.array(z.string()).optional(),
});
const getAllowedMembersForCube = (cubeName) => {
    const cube = cubes[cubeName];
    if (!cube || !(cubeName in tenant.memberByCube))
        return null;
    const tenantMember = tenant.memberByCube[cubeName];
    const baseMembers = [
        ...Object.keys(cube.measures),
        ...Object.keys(cube.dimensions),
        ...Object.keys(cube.timeDimensions),
        ...(tenantMember ? [tenantMember] : []),
    ];
    const linkIdMembers = Object.values(cube.dimensions)
        .map((meta) => meta.link?.idMember)
        .filter((id) => Boolean(id));
    return {
        base: new Set(baseMembers),
        dimensions: new Set([...baseMembers, ...linkIdMembers]),
    };
};
export const reportChartConfigSchema = z.object({
    categoryMember: z.string(),
    colorMember: z.string().optional(),
    valueMember: z.string(),
});
export const reportCubeQuerySchema = z
    .object({
    chartConfig: reportChartConfigSchema.optional(),
    dimensions: z.array(z.string()).optional(),
    filters: z.array(cubeFilterSchema).optional(),
    groupBy: z.array(z.string()).optional(),
    limit: z.number().int().positive().optional(),
    measures: z.array(z.string()).optional(),
    offset: z.number().int().nonnegative().optional(),
    order: z.record(z.string(), cubeOrderDirectionSchema).optional(),
    timeDimensions: z.array(cubeTimeDimensionSchema).optional(),
})
    .superRefine((query, ctx) => {
    const { dimensions, filters, groupBy, measures, order, timeDimensions } = query;
    if (!measures || measures.length === 0) {
        ctx.addIssue({
            code: 'custom',
            message: 'Measures is required',
            path: ['measures'],
        });
        return;
    }
    const cubeName = measures[0].split('.')[0];
    const allowedMembers = getAllowedMembersForCube(cubeName);
    if (!allowedMembers) {
        ctx.addIssue({
            code: 'custom',
            message: `Unknown cube "${cubeName}"`,
            path: ['measures', 0],
        });
        return;
    }
    const { base, dimensions: allowedDimensions } = allowedMembers;
    measures.forEach((member, index) => {
        if (!base.has(member)) {
            ctx.addIssue({
                code: 'custom',
                message: `Member "${member}" does not belong to cube "${cubeName}"`,
                path: ['measures', index],
            });
        }
    });
    dimensions?.forEach((member, index) => {
        if (!allowedDimensions.has(member)) {
            ctx.addIssue({
                code: 'custom',
                message: `Member "${member}" does not belong to cube "${cubeName}"`,
                path: ['dimensions', index],
            });
        }
    });
    groupBy?.forEach((member, index) => {
        if (!base.has(member)) {
            ctx.addIssue({
                code: 'custom',
                message: `Member "${member}" does not belong to cube "${cubeName}"`,
                path: ['groupBy', index],
            });
        }
    });
    timeDimensions?.forEach((timeDimension, index) => {
        if (!base.has(timeDimension.dimension)) {
            ctx.addIssue({
                code: 'custom',
                message: `Member "${timeDimension.dimension}" does not belong to cube "${cubeName}"`,
                path: ['timeDimensions', index, 'dimension'],
            });
        }
    });
    filters?.forEach((filter, index) => {
        if (!base.has(filter.member)) {
            ctx.addIssue({
                code: 'custom',
                message: `Member "${filter.member}" does not belong to cube "${cubeName}"`,
                path: ['filters', index, 'member'],
            });
        }
    });
    Object.keys(order ?? {}).forEach((member) => {
        if (!base.has(member)) {
            ctx.addIssue({
                code: 'custom',
                message: `Member "${member}" does not belong to cube "${cubeName}"`,
                path: ['order', member],
            });
        }
    });
})
    .transform((data) => ({
    ...data,
    measures: data.measures ?? [],
}));
export const reportExportFormatSchema = z.enum(['csv', 'pdf']);
export const reportPdfLayoutSchema = z.enum(['table', 'record']);
export const reportPdfOrientationSchema = z.enum(['landscape', 'portrait']);
export const reportFieldPlacementSchema = z.enum(['inline', 'expanded']);
const fieldPlacementsSchema = z.record(z.string(), reportFieldPlacementSchema);
/**
 * How the report lays its fields out on screen, edited in the Fields tab. Order is stored rather
 * than read back from the query, whose `dimensions`/`timeDimensions` split loses where dates sat.
 */
export const reportDisplayConfigSchema = z.object({
    fieldOrder: z.array(z.string()).optional(),
    fieldPlacements: fieldPlacementsSchema.default({}),
});
/**
 * The PDF's own layout. Placement is seeded from {@link reportDisplayConfigSchema} on first export
 * and saved here from then on, so tweaking a column for print never reshapes the report on screen.
 */
export const reportPdfConfigSchema = z.object({
    fieldOrder: z.array(z.string()).optional(),
    fieldPlacements: fieldPlacementsSchema.optional(),
    layout: reportPdfLayoutSchema.default('table'),
    orientation: reportPdfOrientationSchema.default('landscape'),
    recordsPerPage: z.number().int().min(1).max(20).optional(),
});
export const reportExportSchema = z.object({
    format: reportExportFormatSchema,
    groupByMembers: z.array(z.string()).default([]),
    pdfConfig: reportPdfConfigSchema.optional(),
    query: reportCubeQuerySchema,
    reportName: z.string().default('Report'),
    selectedMembers: z.array(z.string()).min(1, 'At least one member must be selected'),
});
const MAX_FILTER_VALUES_LIMIT = 50;
export const reportFilterValuesSchema = z
    .object({
    cubeName: z
        .string()
        .refine((name) => name in cubes && name in tenant.memberByCube, { message: 'Unknown cube' }),
    limit: z.number().int().positive().max(MAX_FILTER_VALUES_LIMIT).optional().default(25),
    member: z.string(),
    search: z.string().optional(),
})
    .superRefine((data, ctx) => {
    const cube = cubes[data.cubeName];
    if (!cube)
        return;
    const allMembers = new Set([
        ...Object.keys(cube.dimensions),
        ...Object.keys(cube.timeDimensions),
    ]);
    if (!allMembers.has(data.member)) {
        ctx.addIssue({
            code: 'custom',
            message: `Member "${data.member}" does not belong to cube "${data.cubeName}"`,
            path: ['member'],
        });
    }
});
